import { useState, useEffect, useCallback } from "react";
import { CSS } from "./styles";
import {
  supabase,
  getMusicians, getVenues, getVenueEquipment, getEvents,
  createEvent as dbCreateEvent, updateEvent as dbUpdateEvent,
  deleteEvent as dbDeleteEvent, setEventEquipment,
  deleteMusician, deleteVenue, addTrack, joinEventAsParticipant,
  getAppSetting, getCurrentSession, getMyProfile, linkAuthUser, sessionIsValid,
  signOut as dbSignOut,
} from "./lib/supabase";

import AuthScreen, { LoginModal, RegisterStepsModal } from "./components/AuthScreen";
import NextEvent from "./components/NextEvent";
import LiveView from "./components/LiveView";
import EventsList from "./components/EventsList";
import MusiciansList from "./components/MusiciansList";
import ProfileView from "./components/ProfileView";
import SetlistView from "./components/SetlistView";
import EventPreview from "./components/EventPreview";
import LandingPage from "./components/LandingPage";
import BottomNav from "./ui/BottomNav";
import JammoMark from "./ui/JammoMark";
import { chiudiAvvio, SCRITTA_JAMMO } from "./avvio";
import { isEventOrganizer } from "./helpers";
import VenuesList from "./components/VenuesList";

function StyleTag() { return <style dangerouslySetInnerHTML={{ __html: CSS }} />; }
function Favicon() { return <link rel="icon" href="/icona_nera_trasp.png" />; }
// Schermata di caricamento. All'apertura dell'app sopra c'e' la schermata di
// avvio di public/index.html (la J che cade), che parte prima di React: questa
// sta sotto, identica nella posizione, e quando se ne va (l'app e' pronta) la
// schermata di avvio si dissolve. Se il caricamento torna dopo, a app gia'
// aperta, si vede questa: la J ferma con le onde che pulsano.
function LoadingScreen() {
  useEffect(()=>()=>{
    // Si chiude solo se nessuna schermata di caricamento e' rimasta montata:
    // in sviluppo React monta e smonta due volte, e non deve bastare.
    setTimeout(()=>{ if(!document.querySelector('.avvio-fondo')) chiudiAvvio(); },0);
  },[]);
  const onda = (classi, d) => (
    <svg className={`avvio-onda ${classi}`} viewBox="0 0 512 512" aria-hidden="true"><path d={d} strokeWidth="26"/></svg>
  );
  return (
    <div className="avvio-fondo fermo" role="status" aria-label="Caricamento">
      <div className="avvio-marchio">
        <svg className="avvio-j" viewBox="0 0 512 512" aria-hidden="true">
          <circle cx="311" cy="90" r="46" fill="#ED4D1D" stroke="none"/>
          <path d="M311 200 L311 346 A100 100 0 0 1 111 346" strokeWidth="88"/>
        </svg>
        {onda("sx dentro","M245 38 A84 84 0 0 0 245 142")}
        {onda("dx dentro","M377 38 A84 84 0 0 1 377 142")}
        {onda("sx fuori","M201 32 A124 124 0 0 0 201 148")}
        {onda("dx fuori","M421 32 A124 124 0 0 1 421 148")}
        <svg className="avvio-scritta" viewBox={SCRITTA_JAMMO.viewBox} aria-hidden="true"><path d={SCRITTA_JAMMO.d}/></svg>
      </div>
    </div>
  );
}

// Legge l'ID evento dall'hash URL: #/evento/abc123
function getLiveEventId() {
  const hash = window.location.hash;
  const match = hash.match(/^#\/live\/(.+)$/);
  return match ? match[1] : null;
}

function getEventIdFromHash() {
  const hash = window.location.hash;
  const match = hash.match(/^#\/evento\/(.+)$/);
  return match ? match[1] : null;
}

// Imposta l'hash URL
function setHash(hash) {
  window.history.replaceState(null, '', hash || window.location.pathname);
}

// Chiave del vecchio sistema di accesso, quando l'utente veniva salvato nel
// browser senza nessuna verifica. Ora serve solo per accorgersi di chi non ha
// ancora rifatto l'accesso e riproporgli la sua email.
const LEGACY_USER_KEY = "jamup_user";

export default function App() {
  // Chi sei lo decide la sessione verificata da Supabase, non piu' un dato
  // salvato nel browser che chiunque poteva scriversi a mano.
  const [currentUser,setCurrentUser]=useState(null);
  const [authReady,setAuthReady]=useState(false);
  const [musicians,setMusicians]=useState([]);
  const [venues,setVenues]=useState([]);
  const [venueEquipment,setVenueEquipment]=useState({});
  const [events,setEvents]=useState([]);
  // Parte gia' "in caricamento": se partisse da false, fra la verifica
  // dell'accesso e il caricamento delle serate l'app si disegnerebbe vuota per
  // un istante e la schermata di avvio ripartirebbe da capo. Chi non ha fatto
  // l'accesso non arriva mai a questo controllo.
  const [appLoading,setAppLoading]=useState(true);

  const [view,setView]=useState("home");
  const [selectedEvent,setSelectedEvent]=useState(null);
  const [cityFilter,setCityFilter]=useState("tutti");
  const [dateFilter,setDateFilter]=useState("tutti");
  const [mineFilter,setMineFilter]=useState("tutte");
  const [defaultVenueBg,setDefaultVenueBg]=useState(null);

  // Deep link: evento nell'hash URL
  const [liveEventId] = useState(()=>getLiveEventId()); // per QR code / accesso esterno
  const [showLiveId, setShowLiveId] = useState(null); // per accesso in-app
  const [deepLinkEventId] = useState(()=>getEventIdFromHash());
  const [showPreview] = useState(()=>!!getEventIdFromHash());
  const [authMode, setAuthMode] = useState(()=>window.location.hash==="#/registrati"?"register":"login");
  const [showLanding, setShowLanding] = useState(()=>window.location.hash==="#/registrati"||window.innerWidth>=768);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [hasVisitedEvents, setHasVisitedEvents] = useState(false);
  // Email gia' verificata a cui manca il profilo: si riprende da li' invece di
  // chiedere un altro codice.
  const [pendingEmail, setPendingEmail] = useState("");

  const loadData=useCallback(async()=>{
    try{
      const [m,v,e,defBg]=await Promise.all([getMusicians(),getVenues(),getEvents(),getAppSetting('default_venue_bg')]);
      setMusicians(m);setVenues(v);setEvents(e);setDefaultVenueBg(defBg||null);
      const eqMap={};
      await Promise.all(v.map(async(venue)=>{eqMap[venue.id]=await getVenueEquipment(venue.id);}));
      setVenueEquipment(eqMap);
      return e; // ritorna eventi per uso post-login
    }catch(err){console.error(err);}
  },[]);

  const findNextEvent = (evs, user) => {
    const today = new Date(); today.setHours(0,0,0,0);
    return evs.filter(ev => {
      const isOrg = isEventOrganizer(ev, user.id);
      const isPart = (ev.event_participants||[]).some(p => p.musician_id === user.id);
      const evDate = new Date(ev.date); evDate.setHours(0,0,0,0);
      return (isOrg || isPart) && evDate >= today;
    }).sort((a,b) => new Date(a.date) - new Date(b.date))[0] || null;
  };

  // All'avvio ripristina la sessione. Se c'e', recupera il profilo collegato.
  // La sessione si rinnova da sola: il codice via email non viene richiesto
  // di nuovo finche' l'utente non esce volontariamente.
  useEffect(()=>{
    let annullato=false;
    (async()=>{
      try{
        const session=await getCurrentSession();
        if(!session) return;
        let profilo=await getMyProfile();
        // Se la sessione e' nata fuori dal percorso col codice — per esempio
        // cliccando il link dentro l'email — l'aggancio al profilo esistente
        // non e' ancora avvenuto. Lo facciamo qui, cosi' l'utente ritrova le
        // sue cose comunque sia entrato.
        if(!profilo){ await linkAuthUser(); profilo=await getMyProfile(); }
        if(annullato) return;
        if(profilo){ setCurrentUser(profilo); localStorage.removeItem(LEGACY_USER_KEY); }
        else {
          // Nessun profilo collegato. Prima di proporre la registrazione va
          // verificato col server che l'account esista ancora: se e' stato
          // cancellato, il lasciapassare resta comunque nel browser fino alla
          // scadenza e senza questo controllo si finirebbe in un vicolo cieco,
          // con una registrazione che poi fallisce.
          // Il controllo serve solo qui: se un profilo esiste, l'account
          // esiste per forza, perche' cancellandolo il collegamento si azzera.
          if(await sessionIsValid()){
            if(!annullato) setPendingEmail(session.user?.email || "");
          } else {
            try{ await dbSignOut(); }catch(e){ console.error(e); }
          }
        }
      }catch(e){ console.error(e); }
      finally{ if(!annullato) setAuthReady(true); }
    })();

    const {data:sub}=supabase.auth.onAuthStateChange((event)=>{
      if(event==='SIGNED_OUT'){ setCurrentUser(null); setSelectedEvent(null); setView("home"); }
    });
    return ()=>{ annullato=true; sub?.subscription?.unsubscribe(); };
  },[]);

  useEffect(()=>{
    if(currentUser){
      setAppLoading(true);
      loadData().then(evs=>{
        if(deepLinkEventId && evs){
          const ev = evs.find(e=>e.id===deepLinkEventId);
          if(ev){ setSelectedEvent(ev); setHash(''); }
        } else if(evs){
          const next = findNextEvent(evs, currentUser);
          if(next) setSelectedEvent(next);
        }
        setAppLoading(false);
      });
    }
  },[currentUser,loadData,deepLinkEventId]);

  const handleLogin=(user)=>{
    setPendingEmail("");
    localStorage.removeItem(LEGACY_USER_KEY);
    sessionStorage.removeItem(LEGACY_USER_KEY);
    window.scrollTo(0,0);
    setCurrentUser(user);
  };
  const handleLogout=async()=>{
    localStorage.removeItem(LEGACY_USER_KEY);
    sessionStorage.removeItem(LEGACY_USER_KEY);
    // Se l'account e' appena stato cancellato la sessione non esiste piu':
    // l'uscita fallisce ed e' normale, non deve bloccare il resto.
    try{ await dbSignOut(); }catch(e){ console.error(e); }
    setPendingEmail("");
    setCurrentUser(null);setSelectedEvent(null);setView("home");
  };
  const handleUserUpdate=(u)=>{
    setCurrentUser(u);loadData();
  };
  const handleDeleteSelf=async()=>{
    if(currentUser.accountType==="musician")await deleteMusician(currentUser.id);
    else await deleteVenue(currentUser.id);
    await handleLogout();
  };
  const handleCreateEvent=async(data,venueEq)=>{
    const ev=await dbCreateEvent({...data,organizer_type:currentUser.accountType,manager_id:currentUser.accountType==="musician"?currentUser.id:null});
    if(venueEq&&venueEq.length>0)await setEventEquipment(ev.id,venueEq.map(e=>e.item||e));
    // Jam libera automatica
    await addTrack({event_id:ev.id,title:"Jam libera",artist:"",proposed_by:currentUser.id,position:0,status:"draft",type:"free"});
    // Auto-aderisce l'organizzatore musicista
    if(currentUser.accountType==="musician"){
      await joinEventAsParticipant(ev.id,currentUser.id,[]);
    }
    await loadData();
  };
  const handleEditEvent=async(id,data,venueEq)=>{
    await dbUpdateEvent(id,data);
    if(venueEq!==undefined&&data.venue_id)await setEventEquipment(id,venueEq.map(e=>e.item||e));
    await loadData();
  };

  // Finche' non sappiamo se c'e' una sessione valida non disegniamo niente:
  // altrimenti un organizzatore vedrebbe per un istante la vista da ospite.
  if (!authReady) return <><StyleTag/><Favicon/><div className="app-wrapper"><LoadingScreen/></div></>;

  // Live page in-app (loggati)
  if (showLiveId) {
    return <>
      <StyleTag/>
      <LiveView eventId={showLiveId} currentUser={currentUser} defaultVenueBg={defaultVenueBg} onExitLive={()=>setShowLiveId(null)}/>
    </>;
  }

  // Live page: accessibile senza login (QR code / link esterno)
  if (liveEventId) {
    return <>
      <StyleTag/>
      <LiveView eventId={liveEventId} currentUser={currentUser} defaultVenueBg={defaultVenueBg} onExitLive={currentUser ? ()=>{ window.history.replaceState(null,'',window.location.pathname); window.location.reload(); } : null}/>
    </>;
  }

  // Deep link senza login: mostra preview pubblica + modal registrazione/login
  if(!currentUser && deepLinkEventId && showPreview){
    return <>
      <StyleTag/><Favicon/>
      <div className="app-wrapper">
        <EventPreview
          eventId={deepLinkEventId}
          onLogin={()=>setShowLoginModal(true)}
          onRegister={()=>setShowRegisterModal(true)}
        />
        {showRegisterModal && (
          <RegisterStepsModal
            onLogin={handleLogin}
            verifiedEmail={pendingEmail}
            onClose={()=>setShowRegisterModal(false)}
            onSwitchToLogin={()=>{ setShowRegisterModal(false); setShowLoginModal(true); }}
          />
        )}
        {showLoginModal && (
          <LoginModal
            onLogin={handleLogin}
            onClose={()=>setShowLoginModal(false)}
            onSwitchToRegister={(email)=>{ if(email) setPendingEmail(email); setShowLoginModal(false); setShowRegisterModal(true); }}
          />
        )}
      </div>
    </>;
  }

  if(!currentUser && showLanding && !pendingEmail)return<><StyleTag/><Favicon/><div className="app-wrapper"><LandingPage onRegister={()=>setShowLanding(false)} onLogin={()=>{setShowLanding(false);setAuthMode("login");}}/></div></>;
  if(!currentUser)return<><StyleTag/><Favicon/><div className="app-wrapper"><AuthScreen onLogin={handleLogin} initialMode={authMode} verifiedEmail={pendingEmail}/></div></>;
  if(appLoading)return<><StyleTag/><Favicon/><div className="app-wrapper"><LoadingScreen/></div></>;

  const today0 = new Date(); today0.setHours(0,0,0,0);
  const upcomingCount = events.filter(ev => { const d = new Date(ev.date); d.setHours(0,0,0,0); return d >= today0; }).length;

  return (
    <><StyleTag/><Favicon/>
    <div className="app-wrapper">
      <div className="topbar">
        {/* La J al centro: a sinistra resta il vuoto, a destra il profilo in grigio. */}
        <JammoMark height={54} className="topbar-marchio" onClick={()=>{const next=findNextEvent(events,currentUser);setSelectedEvent(next||null);setView("home");}}/>
        {/* Nav desktop — visibile solo su schermi grandi */}
        <div className="topbar-desktop-nav">
          {[["home","Prossima"],["events","Scopri"],["musicians","Musicisti"],...(currentUser.is_admin?[["venues","Location"]]:[])].map(([key,label])=>(
            <button key={key} onClick={()=>{
                if(key==="home"){const next=findNextEvent(events,currentUser);setSelectedEvent(next||null);}
                else setSelectedEvent(null);
                if(key==="events") setHasVisitedEvents(true);
                setView(key);
              }}
              style={{background:"none",border:"none",cursor:"pointer",padding:"6px 14px",borderRadius:8,
                fontSize:16,fontWeight:(view===key&&(key!=="home"?!selectedEvent:true))?700:500,
                color:(view===key&&(key!=="home"?!selectedEvent:true))?"var(--text)":"var(--muted)",
                borderBottom:(view===key&&(key!=="home"?!selectedEvent:true))?"2px solid var(--brand)":"2px solid transparent",
                transition:"all .15s"}}>
              {label}
            </button>
          ))}
        </div>
        <div style={{flex:1}}/>
        {/* Il profilo: grigio, mai arancione. L'arancione e' per le azioni. */}
        <button className={"topbar-profilo"+(view==="profile"&&!selectedEvent?" attivo":"")} aria-label="Il tuo profilo"
          onClick={()=>{setSelectedEvent(null);setView("profile");}}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
            <circle cx="12" cy="8.5" r="4"/><path d="M4.5 20.5c1.2-4 4.2-6 7.5-6s6.3 2 7.5 6"/>
          </svg>
        </button>
      </div>

      <BottomNav view={view} eventCount={hasVisitedEvents ? 0 : upcomingCount} setView={(v)=>{
          if(v==="home"){
            const next=findNextEvent(events, currentUser);
            setSelectedEvent(next||null);
          } else {
            setSelectedEvent(null);
          }
          if(v==="events") setHasVisitedEvents(true);
          setView(v);
        }} selectedEvent={selectedEvent} setSelectedEvent={setSelectedEvent} currentUser={currentUser}/>
      <div className="main">
        {selectedEvent
          ?<>
            {view==="home"&&<div style={{marginBottom:16,textAlign:"center"}}>
              <div style={{fontSize:26,fontWeight:800,color:"var(--text)",lineHeight:1.1}}>Prossima Jam</div>
              <div style={{fontSize:16,color:"var(--muted)",marginTop:6}}>Scegliamo cosa suonare e cosa portare</div>
            </div>}
            <SetlistView event={selectedEvent} currentUser={currentUser} musicians={musicians} venues={venues} venueEquipment={venueEquipment} defaultVenueBg={defaultVenueBg} onRefresh={loadData}
              onEditEvent={handleEditEvent}
              onDeleteEvent={async(id)=>{await dbDeleteEvent(id);setSelectedEvent(null);await loadData();}}
              onOpenLive={(id)=>setShowLiveId(id)}/>
          </>
          :view==="home"
            ?<NextEvent events={events} venues={venues} venueEquipment={venueEquipment} currentUser={currentUser}
                defaultVenueBg={defaultVenueBg}
                onSelectEvent={setSelectedEvent} onCreateEvent={handleCreateEvent}
                onGoToSerate={()=>setView("events")}/>
          :view==="musicians"
            ?<MusiciansList musicians={musicians} currentUser={currentUser} onRefresh={loadData}/>
          :view==="events"
            ?<EventsList events={events} venues={venues} venueEquipment={venueEquipment} currentUser={currentUser}
                defaultVenueBg={defaultVenueBg}
                cityFilter={cityFilter} setCityFilter={setCityFilter}
                dateFilter={dateFilter} setDateFilter={setDateFilter}
                onSelectEvent={setSelectedEvent}
                onCreateEvent={handleCreateEvent}
                onEditEvent={handleEditEvent}
                mineFilter={mineFilter} setMineFilter={setMineFilter}
                onDeleteEvent={async(id)=>{await dbDeleteEvent(id);if(selectedEvent?.id===id)setSelectedEvent(null);await loadData();}}/>
          :view==="venues"
            ?<VenuesList venues={venues} venueEquipment={venueEquipment} currentUser={currentUser} defaultVenueBg={defaultVenueBg} onRefresh={loadData}/>
          :view==="profile"
            ?<ProfileView user={currentUser} venues={venues} venueEquipment={venueEquipment} onLogout={handleLogout} onUserUpdate={handleUserUpdate} onDeleteSelf={handleDeleteSelf} onRefresh={loadData}/>
          :<NextEvent events={events} venues={venues} venueEquipment={venueEquipment} currentUser={currentUser}
              defaultVenueBg={defaultVenueBg}
              onSelectEvent={setSelectedEvent} onCreateEvent={handleCreateEvent}
              onGoToSerate={()=>setView("events")}/>
        }
      </div>
    </div></>
  );
}
